// INVIMP1 (docs/build-briefs/INVIMP1-inventory-import.md; design/mockups/invimp1/). The "Import inventory" screen: choose
// a CSV or XLSX file, match its columns to our item fields, check a preview, import, and download the rows that did not
// import. It is opened from two places and draws the same screen in both: the setup wizard's Drinks step, and the Import
// button beside "+ Add item" on Settings, Inventory items.
//
// This file draws what the server sent. The server reads the file at every step (the browser sends it again each time and
// never sends parsed rows), classifies every row, and does every write through the item editor's own domain functions. The
// rules that need no DOM are ui/src/inventoryImportModel.ts and are unit tested on their own.
//
// An admin screen (a keyboard and pointer may be assumed), built touch-first: every target is at least 44px, nothing
// depends on hover, there is no key handler, and nothing is typed (the file comes from the browser's own picker, columns
// are chosen from a list, the kind is a tap). Text is set with textContent only: names come from the person's own file.
// Sign-out closes the screen. Unresolved original applies remain bound to the actual actor and location
// outside disposable screen state, so only the same actor and scope can recover them.

import {
  applyInventoryImport,
  inspectInventoryImport,
  previewInventoryImport,
  type InventoryImportApplyDto,
  type InventoryImportChoices,
  type InventoryImportInspectDto,
  type InventoryImportPreviewDto,
  type InventoryImportPreviewRowDto,
} from "./api.ts";
import {
  FILE_ACCEPT,
  PREVIEW_TABS,
  assignField,
  costText,
  errorText,
  examplesText,
  failedCsvFileName,
  failedRowsLine,
  fieldOfColumn,
  fileRefusal,
  fileSizeText,
  fieldSource,
  importButtonText,
  matchedLine,
  orNa,
  previewBlock,
  previewBlockedReason,
  readAsLine,
  resultHeadline,
  rowStatusText,
  rowsForTab,
  skipColumn,
  tabCount,
  type Mapping,
  type PreviewTab,
} from "./inventoryImportModel.ts";
import { hasPermission, type Session } from "./session.ts";

export const INVENTORY_IMPORT_PERMISSION = "admin.liquor_product.manage";

let planningScope:
  { locationId: string | undefined; current: () => string | undefined } | undefined;

let getSessionFn: (() => Session | undefined) | undefined;

function mustFind<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (element === null) throw new Error(`expected element for selector "${selector}"`);
  return element;
}

const rootEl = mustFind<HTMLElement>("#inventory-import-screen");

type Phase = "upload" | "mapping" | "preview" | "done";
const PHASE_NUMBER: Record<Phase, number> = { upload: 1, mapping: 2, preview: 3, done: 4 };
const PHASE_LABEL: Record<Phase, string> = {
  upload: "Upload",
  mapping: "Match columns",
  preview: "Preview",
  done: "Done",
};

let open = false;
let phase: Phase = "upload";
let file: File | undefined;
let inspected: InventoryImportInspectDto | undefined;
let mapping: Mapping = {};
let defaultKind: string | undefined;
/** INVIMPORT2: one shelf for every row the file gives none, chosen on the preview when the location prices by shelf. */
let defaultShelf: string | undefined;
let setStartingStock = true;
let preview: InventoryImportPreviewDto | undefined;
let tab: PreviewTab = "all";
let result: InventoryImportApplyDto | undefined;
/** The newest finished import since the screen opened, kept across "Import another file". */
let lastImported: InventoryImportApplyDto | undefined;
let uploadId: string | undefined;
let busy = "";
let problem = "";
let pickerColumn: number | undefined;
/** True while the kind card lists the file's columns so the person can pick the one holding the kind. */
let choosingKindColumn = false;
/** Advanced by every open and reset; an answer that lands for an older one is dropped. */
let generation = 0;
let inspectionSequence = 0;
let onClose: ((imported: InventoryImportApplyDto | undefined, followUp?: "no_shelf") => void) | undefined;

interface OriginalInventoryApply {
  actor: Pick<Session, "apiBaseUrl" | "tenantId" | "userId">;
  file: File;
  choices: InventoryImportChoices;
  uploadId: string;
  busy: boolean;
  problem: string;
  result?: InventoryImportApplyDto;
}
// Retained outside the disposable sheet, never exposed to a different actor or location.
const originalApplies = new Map<string, OriginalInventoryApply>();
let previewIntent: { file: File; choices: InventoryImportChoices } | undefined;
function applyScopeKey(): string | undefined {
  const active = session();
  return active === undefined
    ? undefined
    : JSON.stringify([
        active.apiBaseUrl,
        active.tenantId,
        active.userId,
        planningScope?.locationId ?? null,
      ]);
}
function originalApply(): OriginalInventoryApply | undefined {
  const key = applyScopeKey();
  return key === undefined ? undefined : originalApplies.get(key);
}

function session(): Session | undefined {
  return getSessionFn?.();
}

function current(gen: number, token: string): boolean {
  return (
    (planningScope === undefined || planningScope.locationId === planningScope.current()) &&
    open &&
    gen === generation &&
    session()?.token === token
  );
}

// ---------------------------------------------------------------------
// Small DOM helpers
// ---------------------------------------------------------------------

interface ElOptions {
  cls?: string;
  text?: string;
  testid?: string;
  attrs?: Record<string, string>;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: ElOptions = {},
  ...children: Array<Node | string>
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (options.cls !== undefined) node.className = options.cls;
  if (options.text !== undefined) node.textContent = options.text;
  if (options.testid !== undefined) node.dataset["testid"] = options.testid;
  for (const [name, value] of Object.entries(options.attrs ?? {})) node.setAttribute(name, value);
  for (const child of children) node.append(child);
  return node;
}

function button(
  label: string,
  cls: string,
  testid: string,
  onTap: () => void,
  disabled = false,
): HTMLButtonElement {
  const node = el("button", { cls, text: label, testid, attrs: { type: "button" } });
  node.disabled = disabled;
  node.addEventListener("click", onTap);
  return node;
}

function pill(text: string, tone: "new" | "upd" | "err" | "req" | "ok"): HTMLElement {
  return el("span", { cls: `ii-pill ii-pill-${tone}`, text });
}

// ---------------------------------------------------------------------
// Opening, closing, resetting
// ---------------------------------------------------------------------

function clearState(): void {
  phase = "upload";
  file = undefined;
  inspected = undefined;
  mapping = {};
  defaultKind = undefined;
  defaultShelf = undefined;
  setStartingStock = true;
  preview = undefined;
  tab = "all";
  result = undefined;
  uploadId = undefined;
  previewIntent = undefined;
  busy = "";
  problem = "";
  pickerColumn = undefined;
  choosingKindColumn = false;
}

export function openInventoryImport(options: {
  /** The signed-in session of whoever opened the screen (the wizard's or Settings'). */
  getSession: () => Session | undefined;
  getPlanningLocationId?: () => string | undefined;
  onClose: (imported: InventoryImportApplyDto | undefined, followUp?: "no_shelf") => void;
  /** The look of the screen that opened it (the setup wizard's light theme), so the import does not switch colors. */
  theme?: string;
}): void {
  generation = generation + 1;
  if (options.theme === undefined) delete rootEl.dataset["theme"];
  else rootEl.dataset["theme"] = options.theme;
  getSessionFn = options.getSession;
  planningScope =
    options.getPlanningLocationId === undefined
      ? undefined
      : { locationId: options.getPlanningLocationId(), current: options.getPlanningLocationId };
  clearState();
  lastImported = undefined;
  open = true;
  onClose = options.onClose;
  rootEl.hidden = false;
  document.body.classList.add("inventory-import-open");
  render();
}

function close(followUp?: "no_shelf"): void {
  const key = applyScopeKey();
  const original = originalApply();
  if (original?.result !== undefined && key !== undefined) originalApplies.delete(key);
  const imported = lastImported;
  const callback = onClose;
  hide();
  callback?.(imported, followUp);
}

function hide(): void {
  generation = generation + 1;
  delete rootEl.dataset["theme"];
  open = false;
  onClose = undefined;
  getSessionFn = undefined;
  planningScope = undefined;
  lastImported = undefined;
  clearState();
  rootEl.replaceChildren();
  rootEl.hidden = true;
  document.body.classList.remove("inventory-import-open");
}

/** Sign-out removes the visible draft; actor-bound original applies remain recoverable. */
export function resetInventoryImport(): void {
  hide();
}

// ---------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------

async function chooseFile(chosen: File, sheet?: string): Promise<void> {
  const active = session();
  if (active === undefined) return;
  const refusal = fileRefusal(chosen);
  if (refusal !== "") {
    problem = refusal;
    render();
    return;
  }
  const gen = generation;
  const requestId = ++inspectionSequence;
  busy = "Reading file";
  problem = "";
  render();
  const response = await inspectInventoryImport(active.apiBaseUrl, active.tenantId, chosen, sheet);
  if (!current(gen, active.token) || requestId !== inspectionSequence) return;
  busy = "";
  if (response.status !== 200 || response.body.sheet === undefined) {
    problem = errorText(response.status, response.body.message);
    if (sheet === undefined) {
      file = undefined;
      inspected = undefined;
    }
    render();
    return;
  }
  file = chosen;
  inspected = response.body as InventoryImportInspectDto;
  uploadId = undefined;
  previewIntent = undefined;
  render();
}

function goToMapping(): void {
  if (inspected === undefined) return;
  mapping = { ...inspected.suggestedMapping };
  defaultKind = undefined;
  defaultShelf = undefined;
  preview = undefined;
  problem = "";
  phase = "mapping";
  render();
}

function stockToggleAvailable(): boolean {
  const active = session();
  return (
    active !== undefined &&
    mapping["startingStock"] !== undefined &&
    hasPermission(active, "inv.movement.record")
  );
}

async function runPreview(): Promise<void> {
  const active = session();
  if (active === undefined || file === undefined || inspected === undefined) return;
  if (originalApply() !== undefined || busy !== "") return;
  if (previewBlockedReason(mapping, defaultKind) !== "") return;
  if (planningScope !== undefined && planningScope.locationId !== planningScope.current()) {
    problem = "Location changed. Close and reopen the import.";
    render();
    return;
  }
  const gen = generation;
  busy = "Checking";
  problem = "";
  render();
  const previewFile = file;
  const choices: InventoryImportChoices = {
    ...(planningScope?.locationId === undefined ? {} : { locationId: planningScope.locationId }),
    sheet: inspected.sheet.name,
    mapping: { ...mapping },
    defaultItemKind: defaultKind,
    ...(defaultShelf === undefined ? {} : { defaultShelfTier: defaultShelf }),
    setStartingStock: stockToggleAvailable() && setStartingStock,
  };
  uploadId = undefined;
  previewIntent = undefined;
  const response = await previewInventoryImport(
    active.apiBaseUrl,
    active.tenantId,
    previewFile,
    choices,
  );
  if (!current(gen, active.token)) return;
  busy = "";
  if (
    response.status !== 200 ||
    response.body.counts === undefined ||
    typeof response.body.uploadId !== "string" ||
    response.body.uploadId.length === 0
  ) {
    problem = errorText(response.status, response.body.message);
    render();
    return;
  }
  preview = response.body as InventoryImportPreviewDto;
  uploadId = preview.uploadId;
  previewIntent = { file: previewFile, choices };
  tab = "all";
  phase = "preview";
  render();
}

async function runApply(): Promise<void> {
  const active = session();
  const key = applyScopeKey();
  if (active === undefined || key === undefined) return;
  if (planningScope !== undefined && planningScope.locationId !== planningScope.current()) {
    problem = "Location changed. Reopen this import at its original location to recover it.";
    render();
    return;
  }
  let original = originalApplies.get(key);
  if (original === undefined) {
    if (uploadId === undefined || previewIntent === undefined || busy !== "") return;
    original = {
      actor: { apiBaseUrl: active.apiBaseUrl, tenantId: active.tenantId, userId: active.userId },
      file: previewIntent.file,
      choices: structuredClone(previewIntent.choices),
      uploadId,
      busy: false,
      problem: "",
    };
    originalApplies.set(key, original);
  }
  if (original.busy || original.result !== undefined) return;
  original.busy = true;
  original.problem = "";
  render();
  const response = await applyInventoryImport(
    original.actor.apiBaseUrl,
    original.actor.tenantId,
    original.file,
    original.choices,
    original.uploadId,
  );
  original.busy = false;
  if (
    (response.status === 200 || response.status === 201) &&
    response.body.uploadId === original.uploadId &&
    response.body.added !== undefined
  ) {
    original.result = response.body as InventoryImportApplyDto;
  } else {
    original.problem = `Original import is unconfirmed. ${errorText(response.status, response.body.message)} Retry the original import before choosing another file.`;
  }
  if (open && applyScopeKey() === key) render();
}

function downloadFailedRows(): void {
  if (result === undefined) return;
  const blob = new Blob([result.failedRowsCsv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = failedCsvFileName(result.fileName);
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------

function header(): HTMLElement {
  const bar = el("div", { cls: "ii-bar" });
  bar.append(
    el("span", { cls: "ii-bar-title", text: "Import inventory" }),
    el("span", {
      cls: "ii-bar-sub",
      text: `${PHASE_NUMBER[phase]} of 4 ${PHASE_LABEL[phase]}`,
      testid: "inventory-import-step",
    }),
    el("span", { cls: "ii-sp" }),
  );
  if (phase !== "done") {
    bar.append(button("Cancel", "ii-ghost", "inventory-import-cancel", close));
  }
  return bar;
}

function rail(): HTMLElement {
  const wrap = el("ol", { cls: "ii-rail", attrs: { "aria-label": "Import steps" } });
  for (const step of ["upload", "mapping", "preview", "done"] as const) {
    const n = PHASE_NUMBER[step];
    const currentStep = phase === step;
    const node = el("li", {
      cls: n <= PHASE_NUMBER[phase] ? "ii-step ii-step-done" : "ii-step",
      text: `${n} ${PHASE_LABEL[step]}${currentStep ? " (Current)" : ""}`,
      attrs: currentStep ? { "aria-current": "step" } : {},
    });
    wrap.append(node);
  }
  return wrap;
}

function problemLine(): HTMLElement | undefined {
  if (problem === "") return undefined;
  return el("div", {
    cls: "ii-problem",
    text: problem,
    testid: "inventory-import-problem",
    attrs: { role: "alert" },
  });
}

function render(): void {
  if (!open) return;
  rootEl.replaceChildren();
  const original = originalApply();
  if (original?.result !== undefined) {
    result = original.result;
    lastImported = result;
    phase = "done";
  }
  const body = el("div", { cls: "ii-body" });
  rootEl.append(header(), body);
  if (original !== undefined && original.result === undefined) {
    body.append(
      el("p", {
        cls: "admin-field-help",
        text: original.busy
          ? "Importing the original file."
          : original.problem ||
            "The original import is unconfirmed. Retry it with the original file and choices.",
      }),
      button(
        original.busy ? "Importing" : "Retry original import",
        "ii-primary",
        "inventory-import-retry-original",
        () => void runApply(),
        original.busy,
      ),
    );
    return;
  }
  body.append(rail());
  const note = problemLine();
  if (phase === "upload") drawUpload(body);
  else if (phase === "mapping") drawMapping(body);
  else if (phase === "preview") drawPreview(body);
  else drawDone(body);
  if (note !== undefined) body.prepend(note);
  if (pickerColumn !== undefined) rootEl.append(drawPicker(pickerColumn));
}

function drawUpload(body: HTMLElement): void {
  const input = el("input", {
    cls: "ii-file-input",
    testid: "inventory-import-file-input",
    attrs: { type: "file", accept: FILE_ACCEPT, "aria-label": "Choose file" },
  });
  input.addEventListener("change", () => {
    const chosen = input.files?.[0];
    if (chosen !== undefined) void chooseFile(chosen);
    input.value = "";
  });
  const drop = el("div", { cls: "ii-drop", testid: "inventory-import-drop" });
  drop.append(
    button(
      busy === "" ? (file === undefined ? "Choose file" : "Change file") : busy,
      "ii-primary ii-primary-large",
      "inventory-import-choose",
      () => input.click(),
      busy !== "",
    ),
    el("span", { cls: "ii-tert", text: "Or drop a file here. CSV or XLSX, up to 5 MB" }),
    input,
  );
  drop.addEventListener("dragover", (event) => event.preventDefault());
  drop.addEventListener("drop", (event) => {
    event.preventDefault();
    const dropped = event.dataTransfer?.files[0];
    if (dropped !== undefined && busy === "") void chooseFile(dropped);
  });
  body.append(drop);

  if (file !== undefined && inspected !== undefined) {
    const sheets = inspected.sheets;
    const card = el("div", { cls: "ii-card", testid: "inventory-import-file-card" });
    card.append(
      el(
        "div",
        { cls: "ii-row" },
        el(
          "div",
          {},
          el("div", { cls: "ii-head", text: file.name, testid: "inventory-import-file-name" }),
          el("div", {
            cls: "ii-tert",
            text:
              sheets.length > 1
                ? `${fileSizeText(file.size)}, ${sheets.length} sheets`
                : `${fileSizeText(file.size)}, ${inspected.sheet.rowCount} rows`,
          }),
        ),
      ),
    );
    if (sheets.length > 1) {
      card.append(el("div", { cls: "ii-head ii-gap", text: "Sheet" }));
      const tabs = el("div", { cls: "ii-tabs", attrs: { role: "group", "aria-label": "Sheet" } });
      for (const sheet of sheets) {
        const chip = button(
          `${sheet.name} ${sheet.rowCount} rows`,
          "ii-tab",
          "inventory-import-sheet-tab",
          () => {
            if (file !== undefined) void chooseFile(file, sheet.name);
          },
          busy !== "",
        );
        chip.dataset["sheet"] = sheet.name;
        chip.setAttribute("aria-pressed", sheet.name === inspected.sheet.name ? "true" : "false");
        tabs.append(chip);
      }
      card.append(tabs);
    }
    body.append(card);
  }
  body.append(el("div", { cls: "ii-grow" }));
  body.append(
    el(
      "div",
      { cls: "ii-row ii-actions" },
      el("span", { cls: "ii-sp" }),
      button(
        "Next",
        "ii-primary",
        "inventory-import-next",
        goToMapping,
        file === undefined || busy !== "" || (inspected?.sheet.rowCount ?? 0) === 0,
      ),
    ),
  );
}

type InspectField = InventoryImportInspectDto["fields"][number];

const FIELD_MEANING: Record<string, string> = {
  name: "Item name",
  itemKind: "Configured item kind",
  category: "Configured category",
  containerType: "Configured container type",
  size: "Container size",
  sizeUnit: "Size unit",
  countUnit: "Count unit",
  par: "Location par level",
  cost: "Vendor package cost",
  vendor: "Vendor name",
  vendorSku: "Vendor item code",
  shelfTier: "Configured shelf tier",
  barcode: "Product barcode",
  producer: "Producer name",
  abv: "Alcohol strength",
  countryRegion: "Country or region",
  description: "Item description",
  startingStock: "Location stock quantity",
  sellPrice: "Whole-unit sell price",
};

function appExamples(field: InspectField): string {
  if (field.examplesState === "unavailable" || field.examplesState === undefined)
    return "Examples unavailable";
  if ((field.examples?.length ?? 0) > 0) return (field.examples ?? []).join(", ");
  return FIELD_MEANING[field.id] ?? field.label;
}

function vocabularyCoverage(
  field: InspectField,
  column: InventoryImportInspectDto["sheet"]["columns"][number],
): HTMLElement | null {
  if (!["itemKind", "category", "containerType"].includes(field.id)) return null;
  const wrap = el("div", { cls: "ii-tert ii-vocabulary" });
  if (field.vocabulary === undefined || column.distinctValues === undefined) {
    wrap.textContent = "Vocabulary matches unavailable";
    return wrap;
  }
  const key = (value: string): string => value.trim().toLocaleLowerCase("en");
  const accepted = new Set(
    field.vocabulary.flatMap((entry) => [key(entry.value), key(entry.label)]),
  );
  const values = column.distinctValues;
  const unmatched = values.filter((value) => !accepted.has(key(value)));
  wrap.append(
    el("div", { text: `${values.length - unmatched.length} of ${values.length} values match` }),
  );
  if (unmatched.length > 0) {
    const details = el("details");
    details.append(el("summary", { text: `Unmatched: ${unmatched.length}` }));
    const list = el("ul", { cls: "ii-unmatched-values" });
    for (const value of unmatched) list.append(el("li", { text: value }));
    details.append(list);
    wrap.append(details);
  }
  return wrap;
}

function scrollToTestId(testid: string): void {
  const target = rootEl.querySelector<HTMLElement>(`[data-testid="${testid}"]`);
  if (target === null) return;
  target.scrollIntoView({ block: "center" });
  const focusable = target.matches("button") ? target : target.querySelector<HTMLElement>("button");
  focusable?.focus();
}

/** The plain sentence beside a disabled Preview and the button that goes to the fix. */
function blockedNotice(block: NonNullable<ReturnType<typeof previewBlock>>): HTMLElement {
  return el(
    "span",
    { cls: "ii-blocked", testid: "inventory-import-blocked", attrs: { role: "status" } },
    el("span", { cls: "ii-blocked-reason", text: block.reason, testid: "inventory-import-blocked-reason" }),
    button(block.fixLabel, "ii-secondary", "inventory-import-blocked-fix", () => {
      scrollToTestId(block.fix === "name" ? "inventory-import-columns" : "inventory-import-kind-card");
    }),
  );
}

/** "What kind of items are in this file?" Always visible on the mapping step, not hidden in the column picker. */
function drawKindCard(data: InventoryImportInspectDto): HTMLElement {
  const card = el("div", { cls: "ii-card ii-kind-card", testid: "inventory-import-kind-card" });
  card.append(el("h2", { cls: "ii-head", text: "What kind of items are in this file?" }));
  const kindColumn = mapping["itemKind"];
  if (kindColumn !== undefined) {
    const heading = data.sheet.columns.find((column) => column.index === kindColumn)?.heading ?? "";
    card.append(
      el("div", {
        cls: "ii-tert",
        text:
          data.backbarRecognized === true
            ? `Each row's kind is matched from the file's ${heading} column to your item kinds. Rows with no match use the kind chosen here.`
            : `Each row's kind comes from the file's ${heading} column.`,
        testid: "inventory-import-kind-source",
      }),
    );
  } else if (defaultKind === undefined && mapping["category"] !== undefined) {
    card.append(
      el("div", {
        cls: "ii-tert",
        text: "Each row's kind comes from its category. A row whose category fits no kind is shown as an error in the preview. Or choose one kind for every row.",
        testid: "inventory-import-kind-source",
      }),
    );
  }
  const kinds = el("div", {
    cls: "ii-tabs",
    attrs: { role: "group", "aria-label": "Item kind for every row" },
  });
  for (const kind of data.kinds) {
    const chip = button(kind.label, "ii-tab", "inventory-import-kind-chip", () => {
      defaultKind = defaultKind === kind.value ? undefined : kind.value;
      if (
        defaultKind !== undefined &&
        mapping["itemKind"] !== undefined &&
        data.backbarRecognized !== true
      ) {
        const { itemKind: _released, ...rest } = mapping;
        void _released;
        mapping = rest;
      }
      choosingKindColumn = false;
      render();
    });
    chip.dataset["kind"] = kind.value;
    chip.setAttribute("aria-pressed", defaultKind === kind.value ? "true" : "false");
    kinds.append(chip);
  }
  card.append(
    kinds,
    button(
      "My file has a column for this",
      "ii-secondary",
      "inventory-import-kind-column",
      () => {
        choosingKindColumn = !choosingKindColumn;
        render();
      },
    ),
  );
  if (choosingKindColumn) {
    const list = el("div", {
      cls: "ii-tabs",
      attrs: { role: "group", "aria-label": "Which column holds the kind" },
    });
    for (const column of data.sheet.columns) {
      const chip = button(column.heading, "ii-tab", "inventory-import-kind-column-option", () => {
        mapping = assignField(mapping, "itemKind", column.index);
        defaultKind = undefined;
        choosingKindColumn = false;
        render();
      });
      chip.dataset["column"] = String(column.index);
      list.append(chip);
    }
    card.append(el("div", { cls: "ii-tert", text: "Which column holds the kind?" }), list);
  }
  return card;
}

function drawMapping(body: HTMLElement): void {
  if (inspected === undefined) return;
  const data = inspected;
  const block = previewBlock(mapping, defaultKind);
  const blocked = block?.reason ?? "";
  const summary = el("div", { cls: "ii-row ii-wrap" });
  if (data.backbarRecognized === true) {
    summary.append(
      el("span", {
        cls: "ii-pill ii-pill-ok",
        text: "Backbar export recognized",
        testid: "inventory-import-backbar-recognized",
      }),
    );
  }
  summary.append(
    pill(matchedLine(mapping, data.sheet.columns.length), "ok"),
    el("span", { cls: "ii-sp" }),
    el("span", {
      cls: "ii-tert",
      text: `${data.sheet.rowCount} rows, header in row 1`,
    }),
  );
  body.append(summary);
  body.append(drawKindCard(data));

  const headings = el("div", { cls: "ii-map ii-map-head" });
  headings.append(
    el("strong", { text: "In your file" }),
    el("span", { attrs: { "aria-hidden": "true" } }),
    el("strong", { text: "In Snip & Sip" }),
  );
  body.append(headings);
  const card = el("div", { cls: "ii-card ii-columns", testid: "inventory-import-columns" });
  for (const column of data.sheet.columns) {
    const fieldId = fieldOfColumn(mapping, column.index);
    const field = data.fields.find((candidate) => candidate.id === fieldId);
    const select = el(
      "button",
      {
        cls: field === undefined ? "ii-select ii-select-skip" : "ii-select",
        testid: "inventory-import-column-select",
        attrs: { type: "button", "aria-label": `${column.heading} maps to` },
      },
      el("span", { text: field === undefined ? "Skip this column" : field.label }),
    );
    if (field?.required === true) select.append(pill("Required", "req"));
    select.addEventListener("click", () => {
      pickerColumn = column.index;
      render();
    });
    const target = el("div", { cls: "ii-app-field" }, select);
    if (field !== undefined) {
      target.append(el("div", { cls: "ii-tert ii-samples", text: appExamples(field) }));
      const coverage = vocabularyCoverage(field, column);
      if (coverage !== null) target.append(coverage);
    }
    const suggestion = data.suggestions?.[String(column.index)];
    if (
      suggestion !== undefined &&
      suggestion.fieldId === (fieldId ?? null) &&
      (fieldId === undefined || data.suggestedMapping[fieldId] === column.index)
    ) {
      target.append(
        el("div", { cls: "ii-tert", text: suggestion.reason, testid: "inventory-import-content-match" }),
      );
      if (suggestion.fieldId !== null && column.samples.length > 0) {
        target.append(
          el("div", { cls: "ii-tert", text: `From your file: ${column.samples.slice(0, 2).join(", ")}` }),
        );
      }
    }
    const map = el("div", { cls: "ii-map", testid: "inventory-import-column" });
    map.dataset["column"] = String(column.index);
    map.dataset["field"] = fieldId ?? "";
    map.append(
      el(
        "div",
        {},
        el("div", { cls: "ii-head", text: column.heading }),
        el("div", {
          cls: "ii-tert ii-samples",
          text: examplesText(column.samples),
          testid: "inventory-import-column-examples",
        }),
      ),
      el("div", { cls: "ii-arrow", text: "→", attrs: { "aria-hidden": "true" } }),
      target,
    );
    const read = readAsLine(mapping, data.sheet.columns, fieldId, field?.label ?? "");
    if (read !== undefined) {
      map.append(
        el("div", {
          cls: read.ok ? "ii-read ii-read-ok" : "ii-read ii-read-bad",
          text: read.text,
          testid: "inventory-import-read-as",
          attrs: { "data-ok": read.ok ? "true" : "false" },
        }),
      );
    }
    card.append(map);
  }
  body.append(card);
  body.append(
    el(
      "div",
      { cls: "ii-row ii-actions ii-wrap" },
      button("Back", "ii-secondary", "inventory-import-back", () => {
        phase = "upload";
        problem = "";
        render();
      }),
      el("span", { cls: "ii-sp" }),
      ...(block === undefined ? [] : [blockedNotice(block)]),
      button(
        busy === "" ? "Preview" : busy,
        "ii-primary",
        "inventory-import-preview",
        () => void runPreview(),
        blocked !== "" || busy !== "",
      ),
    ),
  );
}

function drawPicker(column: number): HTMLElement {
  const data = inspected;
  const scrim = el("div", { cls: "ii-scrim", testid: "inventory-import-picker" });
  const card = el("div", {
    cls: "ii-picker",
    attrs: { role: "dialog", "aria-modal": "true", "aria-label": "Choose a field" },
  });
  const heading = data?.sheet.columns[column];
  card.append(
    el("h2", { cls: "ii-picker-title", text: `${heading?.heading ?? "Column"} maps to` }),
    el("div", { cls: "ii-tert", text: examplesText(heading?.samples ?? []) }),
  );
  const fieldNow = fieldOfColumn(mapping, column);
  const list = el("div", { cls: "ii-picker-list" });
  for (const field of data?.fields ?? []) {
    const node = el("button", {
      cls: field.id === fieldNow ? "ii-select ii-select-on" : "ii-select",
      testid: "inventory-import-picker-field",
      attrs: { type: "button" },
    });
    const top = el("span", { cls: "ii-opt-top" }, el("span", { text: field.label }));
    if (field.required) top.append(pill("Required", "req"));
    const source = fieldSource(mapping, data?.sheet.columns ?? [], field.id);
    const lines = el("span", { cls: "ii-opt-lines" }, top);
    if (source !== undefined) {
      lines.append(
        el("span", {
          cls: "ii-opt-source",
          text: `In your file, ${source.heading}: ${source.examples}`,
          testid: "inventory-import-picker-source",
        }),
      );
    }
    lines.append(
      el("span", {
        cls: "ii-tert ii-opt-expects",
        text: appExamples(field),
        testid: "inventory-import-picker-expects",
      }),
    );
    node.append(lines);
    node.dataset["field"] = field.id;
    node.addEventListener("click", () => {
      mapping = assignField(mapping, field.id, column);
      if (field.id === "itemKind") defaultKind = undefined;
      pickerColumn = undefined;
      render();
    });
    list.append(node);
  }
  list.append(
    button("Skip this column", "ii-select ii-select-skip", "inventory-import-picker-skip", () => {
      mapping = skipColumn(mapping, column);
      pickerColumn = undefined;
      render();
    }),
  );
  card.append(list);
  if ((data?.kinds.length ?? 0) > 0) {
    card.append(el("div", { cls: "ii-head ii-gap", text: "Or use one kind for every row" }));
    const kinds = el("div", {
      cls: "ii-tabs",
      attrs: { role: "group", "aria-label": "Item kind" },
    });
    for (const kind of data?.kinds ?? []) {
      const chip = button(kind.label, "ii-tab", "inventory-import-picker-kind", () => {
        defaultKind = defaultKind === kind.value ? undefined : kind.value;
        if (
          defaultKind !== undefined &&
          mapping["itemKind"] !== undefined &&
          data?.backbarRecognized !== true
        ) {
          const { itemKind: _released, ...rest } = mapping;
          void _released;
          mapping = rest;
        }
        pickerColumn = undefined;
        render();
      });
      chip.dataset["kind"] = kind.value;
      chip.setAttribute("aria-pressed", defaultKind === kind.value ? "true" : "false");
      kinds.append(chip);
    }
    card.append(kinds);
  }
  card.append(
    button("Close", "ii-secondary ii-gap", "inventory-import-picker-close", () => {
      pickerColumn = undefined;
      render();
    }),
  );
  scrim.append(card);
  return scrim;
}

function statBox(value: number, label: string, tone: "g" | "b" | "r", testid: string): HTMLElement {
  return el(
    "div",
    { cls: `ii-stat ii-stat-${tone}`, testid },
    el("b", { text: String(value) }),
    el("span", { text: label }),
  );
}

function previewRow(row: InventoryImportPreviewRowDto): HTMLElement {
  const node = el("div", { cls: "ii-prow", testid: "inventory-import-row" });
  node.dataset["status"] = row.status;
  node.dataset["rowNumber"] = String(row.rowNumber);
  const tone = row.status === "new" ? "new" : row.status === "update" ? "upd" : "err";
  const cell = (text: string, cls = ""): HTMLElement =>
    el("span", { cls: `ii-pcell ${cls}`.trim(), text });
  node.append(
    cell(orNa(row.name), "ii-pname"),
    cell(orNa(row.kind)),
    cell(orNa(row.size)),
    cell(orNa(row.par)),
    cell(costText(row.costCents)),
    cell(orNa(row.stock)),
    el("span", { cls: "ii-pcell ii-pstatus" }, pill(rowStatusText(row), tone)),
  );
  if (row.status === "error" && row.reasons.length > 1) {
    node.append(
      el("span", { cls: "ii-pcell ii-pmore ii-tert", text: row.reasons.slice(1).join("; ") }),
    );
  } else if (row.notes.length > 0) {
    node.append(el("span", { cls: "ii-pcell ii-pmore ii-tert", text: row.notes.join("; ") }));
  }
  return node;
}

function drawPreview(body: HTMLElement): void {
  if (preview === undefined) return;
  const data = preview;
  body.append(
    el(
      "div",
      { cls: "ii-row ii-stats" },
      statBox(data.counts.new, "New", "g", "inventory-import-stat-new"),
      statBox(data.counts.update, "Update existing", "b", "inventory-import-stat-update"),
      statBox(data.counts.error, "Errors, skipped", "r", "inventory-import-stat-error"),
    ),
  );
  if (data.newThings.length > 0) {
    body.append(
      el("div", {
        cls: "ii-tert",
        testid: "inventory-import-new-things",
        text: `New: ${data.newThings.map((thing) => `${thing.label} (${thing.kind})`).join(", ")}`,
      }),
    );
  }
  const shelf = data.shelf;
  if (shelf !== undefined && shelf.pricingModel === "shelf" && (shelf.rowsWithoutShelf > 0 || defaultShelf !== undefined)) {
    const shelfCard = el("div", { cls: "ii-card", testid: "inventory-import-shelf" });
    shelfCard.append(
      el("div", {
        cls: "ii-head",
        text:
          shelf.rowsWithoutShelf > 0
            ? `${shelf.rowsWithoutShelf} items have no shelf. This location prices by shelf, so they cannot be sold until each has one.`
            : "Every item has a shelf.",
      }),
      el("div", { cls: "ii-tert", text: "Or use one shelf for every item without one" }),
    );
    const tiers = el("div", { cls: "ii-tabs", attrs: { role: "group", "aria-label": "Shelf" } });
    for (const tier of shelf.tiers) {
      const chip = button(tier.label, "ii-tab", "inventory-import-shelf-option", () => {
        if (busy !== "") return;
        defaultShelf = defaultShelf === tier.value ? undefined : tier.value;
        void runPreview();
      });
      chip.dataset["shelf"] = tier.value;
      chip.setAttribute("aria-pressed", defaultShelf === tier.value ? "true" : "false");
      tiers.append(chip);
    }
    shelfCard.append(tiers);
    body.append(shelfCard);
  }
  const controls = el("div", { cls: "ii-row ii-wrap" });
  const tabs = el("div", { cls: "ii-tabs", attrs: { role: "group", "aria-label": "Rows" } });
  for (const definition of PREVIEW_TABS) {
    const chip = button(
      `${definition.label} ${tabCount(data, definition.id)}`,
      "ii-tab",
      "inventory-import-tab",
      () => {
        tab = definition.id;
        render();
      },
    );
    chip.dataset["tab"] = definition.id;
    chip.setAttribute("aria-pressed", tab === definition.id ? "true" : "false");
    tabs.append(chip);
  }
  controls.append(tabs, el("span", { cls: "ii-sp" }));
  if (stockToggleAvailable()) {
    const label = el("label", { cls: "ii-check", testid: "inventory-import-stock-toggle" });
    const box = el("input", { attrs: { type: "checkbox" } });
    box.checked = setStartingStock;
    box.disabled = busy !== "";
    box.addEventListener("change", () => {
      if (busy !== "") return;
      setStartingStock = box.checked;
      void runPreview();
    });
    label.append(box, el("span", { text: "Set starting stock from the file" }));
    controls.append(label);
  }
  body.append(controls);

  const list = el("div", { cls: "ii-card ii-prows", testid: "inventory-import-rows" });
  list.append(
    el(
      "div",
      { cls: "ii-prow ii-phead" },
      ...["Name", "Kind", "Size", "Par", "Cost", "Stock", "Status"].map((label) =>
        el("span", { cls: "ii-pcell", text: label }),
      ),
    ),
  );
  const shown = rowsForTab(data.rows, tab);
  for (const row of shown) list.append(previewRow(row));
  if (shown.length === 0) list.append(el("div", { cls: "ii-tert ii-empty", text: "No rows in this tab." }));
  if (data.rowsTruncated) {
    list.append(el("div", { cls: "ii-tert ii-empty", text: "More rows than shown" }));
  }
  body.append(list);

  const total = data.counts.new + data.counts.update;
  body.append(
    el(
      "div",
      { cls: "ii-row ii-actions" },
      button("Back", "ii-secondary", "inventory-import-back", () => {
        phase = "mapping";
        problem = "";
        render();
      }),
      el("span", { cls: "ii-sp" }),
      button(
        busy === "" ? importButtonText(data.counts) : busy,
        "ii-primary",
        "inventory-import-apply",
        () => void runApply(),
        total === 0 || busy !== "" || uploadId === undefined || previewIntent === undefined,
      ),
    ),
  );
}

function drawDone(body: HTMLElement): void {
  if (result === undefined) return;
  const done = result;
  body.append(
    el("h1", {
      cls: "ii-title",
      text: resultHeadline(done),
      testid: "inventory-import-result-title",
    }),
    el(
      "div",
      { cls: "ii-row ii-stats" },
      statBox(done.added, "Added", "g", "inventory-import-result-added"),
      statBox(done.updated, "Updated", "b", "inventory-import-result-updated"),
      statBox(done.failed, "Failed", "r", "inventory-import-result-failed"),
    ),
  );
  if ((done.needsPricing ?? 0) > 0) {
    body.append(
      el(
        "div",
        { cls: "ii-card ii-row", testid: "inventory-import-result-needs-pricing" },
        pill("Needs pricing", "req"),
        el("span", {
          cls: "ii-tert",
          text: `${done.needsPricing} imported items need an order unit or pack size before a per-ounce price can be calculated`,
        }),
      ),
    );
  }
  if ((done.needsShelf ?? 0) > 0) {
    body.append(
      el(
        "div",
        { cls: "ii-card ii-row", testid: "inventory-import-result-needs-shelf" },
        pill("Needs a shelf", "req"),
        el("span", {
          cls: "ii-tert",
          text: `${done.needsShelf} imported items have no shelf yet. They cannot be sold until each has one.`,
        }),
        button("Show them", "ii-secondary", "inventory-import-show-no-shelf", () => close("no_shelf")),
      ),
    );
  }
  if (done.stockSet > 0 || done.stockSkipped > 0) {
    body.append(
      el(
        "div",
        { cls: "ii-card ii-row", testid: "inventory-import-result-stock" },
        pill("Starting stock set", "ok"),
        el("span", {
          cls: "ii-tert",
          text:
            done.stockSkipped > 0
              ? `${done.stockSet} items, ${done.stockSkipped} skipped, has history`
              : `${done.stockSet} items`,
        }),
      ),
    );
  }
  if (done.failed > 0) {
    const card = el("div", { cls: "ii-card", testid: "inventory-import-failed" });
    card.append(
      el(
        "div",
        { cls: "ii-row" },
        el(
          "div",
          {},
          el("div", { cls: "ii-head", text: failedRowsLine(done.failed) }),
          el("div", { cls: "ii-tert", text: "A CSV with a Reason column" }),
        ),
        el("span", { cls: "ii-sp" }),
        button(
          "Download failed rows",
          "ii-secondary",
          "inventory-import-download-failed",
          downloadFailedRows,
        ),
      ),
    );
    const table = el("div", { cls: "ii-failed" });
    table.append(
      el(
        "div",
        { cls: "ii-frow ii-phead" },
        el("span", { text: "Row" }),
        el("span", { text: "Name" }),
        el("span", { text: "Reason" }),
      ),
    );
    for (const failedRow of done.failedRows.slice(0, 20)) {
      table.append(
        el(
          "div",
          { cls: "ii-frow", testid: "inventory-import-failed-row" },
          el("span", { text: String(failedRow.rowNumber) }),
          el("span", { text: orNa(failedRow.name) }),
          el("span", { text: failedRow.reason }),
        ),
      );
    }
    card.append(table);
    body.append(card);
  }
  body.append(el("div", { cls: "ii-grow" }));
  body.append(
    el(
      "div",
      { cls: "ii-row ii-actions" },
      button("Import another file", "ii-secondary", "inventory-import-another", () => {
        const keep = onClose;
        const key = applyScopeKey();
        if (key !== undefined && originalApply()?.result !== undefined) originalApplies.delete(key);
        clearState();
        onClose = keep;
        render();
      }),
      el("span", { cls: "ii-sp" }),
      button("Done", "ii-primary", "inventory-import-done", () => close()),
    ),
  );
}
